import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import ArenaxLayout from './ArenaxLayout';
import { ArenaxWordmark } from './ArenaxWordmark';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';

/**
 * Handles return from https://auth.emergentagent.com with `#session_id=...`.
 *
 * Phase 117 — Identity Federation aware:
 *   - When the backend response carries `federated: true`, the user is an
 *     existing Out-Play ecosystem identity (super_admin / academy_owner /
 *     coach / employee / student / parent).  We show a warmer "Welcome back
 *     to Out-Play ArenaX, {name}!" copy and route them to `/arenax` so they
 *     land inside ArenaX with their original role hydrated silently.
 *   - First-time / native players go to `/arenax/player` as before.
 *   - All chrome flips through the theme tokens so the page is legible in
 *     both dark cinematic and light editorial modes.
 */
export default function ArenaxAuthCallback() {
  const { setUser, setToken } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [federatedName, setFederatedName] = useState(null);
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || '';
    const m = hash.match(/session_id=([^&]+)/);
    const sessionId = m ? decodeURIComponent(m[1]) : null;
    if (!sessionId) {
      setError('No session id found. Please retry sign-in.');
      return;
    }
    (async () => {
      try {
        const r = await api.post('/arenax/auth/google-callback', { session_id: sessionId });
        const { access_token, user, federated } = r.data;
        localStorage.setItem('outplay_token', access_token);
        localStorage.setItem('outplay_user', JSON.stringify(user));
        setToken(access_token);
        setUser(user);
        if (federated) {
          setFederatedName(user?.name || null);
          toast.success(`Welcome back to Out-Play ArenaX, ${user?.name || 'friend'}!`);
          // Brief beat so the federated welcome registers, then route home.
          setTimeout(() => {
            window.history.replaceState(null, '', '/arenax');
            navigate('/arenax');
          }, 600);
        } else {
          toast.success(`Welcome ${user?.name || 'player'}!`);
          window.history.replaceState(null, '', '/arenax/player');
          navigate('/arenax/player');
        }
      } catch (err) {
        const detail = err.response?.data?.detail || 'Google sign-in failed.';
        setError(detail);
      }
    })();
  }, [navigate, setToken, setUser]);

  return (
    <ArenaxLayout hideNav>
      <section className="min-h-screen flex items-center justify-center px-5 arenax-themed">
        <div className="text-center max-w-md">
          {!error ? (
            <>
              <div className="inline-flex items-center justify-center mb-5">
                <ArenaxWordmark size="xl" />
              </div>
              <div
                className="text-[10px] uppercase tracking-[0.4em] mb-3 font-semibold"
                style={{ color: `hsl(var(--ax-text-faint))` }}
                data-testid="arenax-callback-eyebrow"
              >
                {federatedName ? 'SIGNED IN TO ARENAX' : 'SIGNING IN TO ARENAX'}
              </div>
              <h1
                className="text-2xl font-black mb-2 font-display"
                style={{ color: `hsl(var(--ax-text))` }}
                data-testid="arenax-callback-heading"
              >
                {federatedName
                  ? `Welcome back, ${federatedName}`
                  : 'Bringing you in…'}
              </h1>
              <p
                className="text-sm mb-2"
                style={{ color: `hsl(var(--ax-text-muted))` }}
              >
                {federatedName
                  ? 'Your Out-Play identity is linked — taking you to the city pulse.'
                  : 'Validating your Google session and provisioning your ArenaX player profile.'}
              </p>
              <p
                className="text-[11px] italic mb-6"
                style={{ color: `hsl(var(--ax-text-faint))` }}
                data-testid="arenax-callback-attribution"
              >
                Powered securely by Out-Play Identity
              </p>
              <Loader2
                className="h-6 w-6 mx-auto animate-spin"
                style={{ color: `hsl(var(--ax-accent-cyan))` }}
              />
            </>
          ) : (
            <>
              <h1
                className="text-2xl font-black mb-2 font-display"
                style={{ color: `hsl(var(--ax-text))` }}
              >
                Sign-in interrupted
              </h1>
              <p
                className="text-sm mb-6"
                style={{ color: 'hsl(0 70% 55%)' }}
                data-testid="arenax-callback-error"
              >
                {error}
              </p>
              <button
                onClick={() => navigate('/arenax/auth/login')}
                className="px-6 py-3 rounded-xl font-bold text-sm"
                style={{
                  background: 'var(--ax-primary-gradient)',
                  color: `hsl(var(--ax-button-text))`,
                }}
              >
                Back to Sign In
              </button>
            </>
          )}
        </div>
      </section>
    </ArenaxLayout>
  );
}
